import { useEffect, useMemo, useState } from "react";
import api, { API } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { toast } from "sonner";
import { Receipt, Download, FileSpreadsheet, Pencil, History, Undo2 } from "lucide-react";
import CreditNoteModal from "../../components/admin/CreditNoteModal";

/**
 * AdminInvoices — "Facturas" menu (admin/superadmin only). Browses every
 * invoice issued (a given month at a time, same endpoint/pattern as
 * MonthlyDocumentsPanel.jsx on the Progreso dashboard, but as its own page
 * and scoped to invoices only — no ticket tab), and is the entry point to
 * generate a factura rectificativa (devolución) on any of them.
 */
const MONTHS_ES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

const fmtEur = (v) => `${(Number(v) || 0).toFixed(2).replace(".", ",")} €`;

const FIELD_LABELS = {
  manual_concept: "Concepto",
  amount_eur: "Importe",
  vat_percent_override: "IVA (%)",
};

const fmtDiffValue = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "number") return String(v).replace(".", ",");
  return String(v);
};
const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch {
    return iso;
  }
};

export default function AdminInvoices() {
  const { user: currentUser } = useAuth();
  const isSuperadmin = currentUser?.role === "superadmin";
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ concept: "", amount: "", vat_percent: "" });
  const [editSaving, setEditSaving] = useState(false);
  const [auditLog, setAuditLog] = useState(null);
  const [creditNoteFor, setCreditNoteFor] = useState(null); // item, or null

  const yearOptions = useMemo(() => {
    const cur = now.getFullYear();
    return [cur, cur - 1, cur - 2];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = () => {
    setLoading(true);
    return api
      .get(`/admin/documents/monthly?year=${year}&month=${month}&kind=invoice`)
      .then(({ data: d }) => setData(d))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month]);

  const downloadPdf = async (item) => {
    const token = localStorage.getItem("token");
    const res = await fetch(`${API}/admin/invoices/${item.id}/pdf`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const prefix = item.document_kind === "credit_note" ? "rectificativa" : "factura";
    a.download = `${prefix}-${item.invoice_number || item.id}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const exportCsv = async () => {
    const token = localStorage.getItem("token");
    const res = await fetch(
      `${API}/admin/documents/monthly.csv?year=${year}&month=${month}&kind=invoice`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    if (!res.ok) return;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `facturas-${year}-${String(month).padStart(2, "0")}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const items = data?.items || [];
  const summary = data?.summary;

  return (
    <div data-testid="admin-invoices-page">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <h1 className="font-display text-3xl text-gray-900 flex items-center gap-2">
            <Receipt className="w-6 h-6 text-brand" /> Facturas
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Todas las facturas emitidas — descarga cualquiera o genera una devolución (factura rectificativa).
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={month}
            onChange={(e) => setMonth(Number(e.target.value))}
            className="border rounded px-3 py-1.5 text-sm bg-white"
            data-testid="invoices-month"
          >
            {MONTHS_ES.map((m, i) => (
              <option key={i + 1} value={i + 1}>{m}</option>
            ))}
          </select>
          <select
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="border rounded px-3 py-1.5 text-sm bg-white"
            data-testid="invoices-year"
          >
            {yearOptions.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <Button variant="outline" size="sm" onClick={exportCsv} data-testid="invoices-export-csv">
            <FileSpreadsheet className="w-4 h-4 mr-1" /> Exportar CSV
          </Button>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-gray-400 italic">Cargando…</p>
      ) : summary ? (
        <div className="text-sm text-brand-ink mb-4 leading-relaxed" data-testid="invoices-summary">
          En <span className="font-semibold">{MONTHS_ES[month - 1]} {year}</span> has emitido{" "}
          <span className="font-mono-data text-brand">{summary.invoices.count}</span> documentos por{" "}
          <span className="font-mono-data text-brand">{fmtEur(summary.invoices.total_eur)}</span>.
        </div>
      ) : null}

      <Card className="border border-gray-200 shadow-none rounded-lg overflow-hidden p-0">
        {!loading && items.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="invoices-table">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-gray-500 border-b bg-gray-50">
                  <th className="text-left py-2 px-3">Nº</th>
                  <th className="text-left py-2 px-3">Tipo</th>
                  <th className="text-left py-2 px-3">Fecha</th>
                  <th className="text-left py-2 px-3">Alumno</th>
                  <th className="text-left py-2 px-3">Concepto</th>
                  <th className="text-right py-2 px-3">Importe</th>
                  <th className="py-2 px-3"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => {
                  const isCreditNote = it.document_kind === "credit_note";
                  return (
                    <tr key={it.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`invoice-row-${it.id}`}>
                      <td className="py-2 px-3 font-mono-data text-brand-ink whitespace-nowrap">
                        <span className="inline-flex items-center gap-1.5" title={it.has_edits ? "Factura editada — abre para ver historial" : ""}>
                          {it.invoice_number || "—"}
                          {it.has_edits && <History className="w-3 h-3 text-amber-600" aria-label="Factura editada" />}
                        </span>
                      </td>
                      <td className="py-2 px-3">
                        {isCreditNote ? (
                          <span className="text-[10px] font-mono-data px-1.5 py-0.5 rounded bg-red-50 text-red-700 border border-red-200">
                            FACTURA RECTIFICATIVA
                          </span>
                        ) : (
                          <span className="text-[10px] font-mono-data px-1.5 py-0.5 rounded bg-brand-soft text-brand-ink border border-[#bcdfd9]">
                            FACTURA
                          </span>
                        )}
                      </td>
                      <td className="py-2 px-3 text-gray-600 whitespace-nowrap">{fmtDate(it.paid_at)}</td>
                      <td className="py-2 px-3 text-gray-700 truncate max-w-[180px]" title={it.user_email || ""}>
                        {it.user_name || it.user_email || "—"}
                      </td>
                      <td className="py-2 px-3 max-w-xs truncate text-gray-700" title={it.concept}>{it.concept}</td>
                      <td className={`py-2 px-3 text-right font-mono-data ${isCreditNote ? "text-red-600" : ""}`}>
                        {fmtEur(it.amount_eur)}
                      </td>
                      <td className="py-2 px-3 text-right whitespace-nowrap">
                        {isSuperadmin && !isCreditNote && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setEditing(it);
                              setAuditLog(null);
                              setEditForm({
                                concept: it.concept || "",
                                amount: String(it.amount_eur ?? ""),
                                vat_percent: it.vat_percent != null ? String(it.vat_percent) : "",
                              });
                            }}
                            title="Editar factura"
                            data-testid={`invoices-edit-${it.id}`}
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        {!isCreditNote && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setCreditNoteFor(it)}
                            title="Generar devolución"
                            className="text-red-600 hover:bg-red-50"
                            data-testid={`invoices-credit-note-${it.id}`}
                          >
                            <Undo2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => downloadPdf(it)} title="Descargar PDF" data-testid={`invoices-download-${it.id}`}>
                          <Download className="w-3.5 h-3.5" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : !loading ? (
          <p className="text-sm text-gray-400 italic text-center py-8" data-testid="invoices-empty">
            Sin facturas emitidas en {MONTHS_ES[month - 1]} {year}.
          </p>
        ) : null}
      </Card>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent data-testid="edit-invoice-dialog">
          <DialogHeader>
            <DialogTitle>Editar factura {editing?.invoice_number || ""}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="ei-concept">Concepto</Label>
              <Input
                id="ei-concept"
                value={editForm.concept}
                onChange={(e) => setEditForm({ ...editForm, concept: e.target.value })}
                data-testid="edit-invoice-concept"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="ei-amount">Importe (€)</Label>
                <Input
                  id="ei-amount"
                  type="number"
                  step="0.01"
                  min="0"
                  value={editForm.amount}
                  onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })}
                  data-testid="edit-invoice-amount"
                />
              </div>
              <div>
                <Label htmlFor="ei-vat">IVA (%)</Label>
                <Input
                  id="ei-vat"
                  type="number"
                  step="0.1"
                  min="0"
                  max="100"
                  value={editForm.vat_percent}
                  onChange={(e) => setEditForm({ ...editForm, vat_percent: e.target.value })}
                  placeholder="0"
                  data-testid="edit-invoice-vat"
                />
              </div>
            </div>
            <p className="text-[11px] text-gray-500">
              El PDF de la factura se regenera automáticamente con los nuevos valores. Deja el IVA vacío para heredar el porcentaje global de Ajustes.
            </p>

            {editing && (
              <details
                className="rounded-md border border-gray-200 bg-gray-50 open:bg-white transition-colors"
                onToggle={async (e) => {
                  if (e.target.open && auditLog === null) {
                    try {
                      const { data: log } = await api.get(`/admin/invoices/${editing.id}/audit-log`);
                      setAuditLog(log);
                    } catch {
                      setAuditLog([]);
                    }
                  }
                }}
                data-testid="audit-log-toggle"
              >
                <summary className="cursor-pointer px-3 py-2 text-xs font-semibold text-brand-ink flex items-center gap-2">
                  <History className="w-3.5 h-3.5" /> Historial de cambios
                </summary>
                <div className="px-3 pb-3 pt-1 space-y-2" data-testid="audit-log-list">
                  {auditLog === null && <p className="text-xs text-gray-400 italic">Cargando…</p>}
                  {Array.isArray(auditLog) && auditLog.length === 0 && (
                    <p className="text-xs text-gray-400 italic">Sin cambios registrados aún.</p>
                  )}
                  {Array.isArray(auditLog) && auditLog.map((entry) => (
                    <div key={entry.id} className="border-l-2 border-brand pl-3 py-1">
                      <p className="text-[11px] text-gray-500 font-mono-data">
                        {new Date(entry.at).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" })}
                        {" · "}
                        <span className="text-brand-ink">{entry.edited_by_email}</span>
                      </p>
                      <ul className="mt-1 space-y-0.5">
                        {Object.entries(entry.changes || {}).map(([field, diff]) => (
                          <li key={field} className="text-xs text-gray-700">
                            <span className="font-medium">{FIELD_LABELS[field] || field}</span>:{" "}
                            <span className="line-through text-red-600 font-mono-data">{fmtDiffValue(diff.before)}</span>
                            {" → "}
                            <span className="text-emerald-700 font-mono-data">{fmtDiffValue(diff.after)}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </details>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)}>Cancelar</Button>
            <Button
              onClick={async () => {
                if (!editing) return;
                const body = {};
                if (editForm.concept !== (editing.concept || "")) body.concept = editForm.concept;
                if (editForm.amount !== "" && Number(editForm.amount) !== editing.amount_eur) body.amount = Number(editForm.amount);
                if (editForm.vat_percent !== "" && Number(editForm.vat_percent) !== editing.vat_percent) body.vat_percent = Number(editForm.vat_percent);
                if (Object.keys(body).length === 0) {
                  toast.info("No hay cambios que guardar");
                  return;
                }
                setEditSaving(true);
                try {
                  await api.put(`/admin/invoices/${editing.id}`, body);
                  toast.success("Factura actualizada — PDF regenerado");
                  setAuditLog(null);
                  setEditing(null);
                  load();
                } catch (err) {
                  toast.error(err?.response?.data?.detail || "No se pudo guardar");
                } finally {
                  setEditSaving(false);
                }
              }}
              disabled={editSaving}
              className="bg-brand hover:bg-brand-hover text-white"
              data-testid="edit-invoice-save"
            >
              {editSaving ? "Guardando…" : "Guardar cambios"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CreditNoteModal
        txId={creditNoteFor?.id}
        invoiceNumber={creditNoteFor?.invoice_number}
        open={!!creditNoteFor}
        onClose={() => setCreditNoteFor(null)}
        onDone={() => { setCreditNoteFor(null); load(); }}
      />
    </div>
  );
}
